/**
 * Admin-impersonation boot handshake.
 *
 * Backend contract (see `models/admin/Users.js#putLoginAs`): an admin can
 * mint a short-TTL (~30 min) impersonation JWT for a target user and is then
 * redirected (or window.open()ed) to V2 with `?impersonation_token=<jwt>` in
 * the URL. The token's `yr_auth_tokens` row carries `source='I'` and a
 * `data` envelope `{ admin_user_id, expires_at, impersonation: true }` so
 * the backend can enforce expiry on each verify.
 *
 * The V2 side needs to:
 *   1. Detect `?impersonation_token` on first boot,
 *   2. Repack it into the Zustand-persist localStorage slot (`yk-auth`) so
 *      `useAuth.init()` picks it up exactly like a normal login,
 *   3. Stash an `impersonation` marker so the persistent yellow banner
 *      survives reloads (sessionStorage — DELIBERATELY not localStorage —
 *      so closing the tab drops the marker, and a brand-new tab on the
 *      same admin doesn't inherit it),
 *   4. Strip the token from the URL via `history.replaceState` so it
 *      doesn't leak into Referer / bookmarks / sharing.
 *
 * MUST run before `useAuth` is module-evaluated — Zustand's `persist`
 * middleware reads localStorage at store-creation time. `main.tsx` imports
 * this module before `migrateLegacyAuth.ts` so the URL handoff wins over a
 * stale legacy session.
 */
const V2_AUTH_KEY = 'yk-auth';
/** sessionStorage marker — `{ active, since, admin_user_id? }` JSON. */
const IMPERSONATION_MARKER_KEY = 'yk-impersonation';
/** URL search param the backend appends to the V2 redirect. */
const TOKEN_PARAM = 'impersonation_token';

/**
 * Read `?impersonation_token=` and, if present, install it as the active
 * session. Idempotent: safe to call multiple times; the marker check stops
 * a second call from re-importing the same token.
 */
export function captureImpersonationToken(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;

  let token: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    token = params.get(TOKEN_PARAM);
    if (!token) return;
  } catch {
    return;
  }

  // Repack the token into the V2 auth slot. Zustand's persist middleware
  // will read this on store creation. We deliberately do NOT prefill `user`
  // here — `useAuth.init()` calls `GET auth/verify` on boot, which will
  // populate the correct target-user payload + flip the status to
  // `authenticated`. Falling through with `user: null` is the SAME shape
  // `migrateLegacyAuth.ts` writes when only a legacy token (no userData)
  // exists, and `init()` handles it transparently.
  try {
    localStorage.setItem(
      V2_AUTH_KEY,
      JSON.stringify({ state: { token, user: null }, version: 0 }),
    );
  } catch {
    // Private mode / storage disabled — fall through. The URL is still in
    // memory so the user can retry; we just can't persist the session.
    return;
  }

  // sessionStorage marker — drives the yellow banner across reloads in this
  // tab only. A new tab on the same admin must NOT inherit the
  // "impersonating" UI (the token does carry over via localStorage, but
  // that's the backend's expiry gate's problem; the banner stays scoped
  // to the tab that received the handoff).
  try {
    sessionStorage.setItem(
      IMPERSONATION_MARKER_KEY,
      JSON.stringify({
        active: true,
        since: new Date().toISOString(),
      }),
    );
  } catch {
    // Marker missing → banner won't render, but the impersonation session
    // still works. Acceptable degradation.
  }

  // Strip the token from the URL so it doesn't leak into Referer headers,
  // browser history, or any "copy current URL" affordance the user might
  // hit while impersonating. Keep the rest of the querystring + path +
  // hash intact in case the deep-link carried other params.
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(TOKEN_PARAM);
    const next = url.pathname + (url.search || '') + (url.hash || '');
    window.history.replaceState(window.history.state, '', next);
  } catch {
    // History API blocked (e.g. exotic embed) — non-fatal; the rest still
    // works, the URL just stays visible until the user navigates.
  }
}

/** Read the sessionStorage marker. Returns null if not impersonating. */
export function readImpersonationMarker(): { active: boolean; since: string } | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_MARKER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { active?: boolean; since?: string } | null;
    if (!parsed || parsed.active !== true || typeof parsed.since !== 'string') return null;
    return { active: true, since: parsed.since };
  } catch {
    return null;
  }
}

/** Drop the sessionStorage marker. The caller is responsible for `logout()`. */
export function clearImpersonationMarker(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(IMPERSONATION_MARKER_KEY);
  } catch {
    /* ignore */
  }
}

// Side-effect: run the capture the moment this module is evaluated. The
// import order in `main.tsx` is what guarantees this runs BEFORE `useAuth`
// is reached transitively — ES module imports are evaluated in source
// order, and any imports placed after this line haven't started yet.
captureImpersonationToken();
